/**
 * Main game scene composition.
 * Renders all game objects: ships, planet, moon, asteroids, borders, effects.
 */

import { useGameStore } from '../stores/gameStore';
import { SpaceBackground } from './background/SpaceBackground.js';
import { SceneryRenderer } from './scenery/SceneryRenderer.js';
import { TopDownCamera } from './camera/TopDownCamera';
import { Planet } from './Planet';
import { AsteroidField } from './Asteroid';
import { MapBorder } from './MapBorder';
import { PlayerShip } from './PlayerShip';
import { RemoteShip } from './RemoteShip';
import { DebugWireframes } from './debug/DebugWireframes';
import { PostprocessingPipeline } from './postprocessing/PostprocessingPipeline';
import { ParticleManagerInit } from './effects/ParticleManagerInit';
import { ProjectileEffects } from './effects/ProjectileEffects';
import { ImpactFlash } from './effects/ImpactFlash';

// Hemisphere light: blue-purple starlight from above, near-black from below
// Three.js r170 uses physically correct lighting (1/PI BRDF divisor) — need high values
const HEMISPHERE_SKY_COLOR = '#1a1a3e';
const HEMISPHERE_GROUND_COLOR = '#080810';
const HEMISPHERE_INTENSITY = 4.0;

// Key light: warm stellar light from upper-right (HDR intensity)
const KEY_LIGHT_COLOR = '#ffeedd';
const KEY_LIGHT_INTENSITY = 12.0;
const KEY_LIGHT_POSITION: [number, number, number] = [200, 80, -150];

// Fill light: cool blue nebula fill from opposite side
const FILL_LIGHT_COLOR = '#4466aa';
const FILL_LIGHT_INTENSITY = 3.0;
const FILL_LIGHT_POSITION: [number, number, number] = [-100, 60, 100];

interface GameSceneProps {
  readonly debugWireframes?: boolean;
}

export function GameScene({ debugWireframes }: GameSceneProps) {
  const worldSetup = useGameStore((s) => s.worldSetup);
  const localShipAlive = useGameStore((s) => s.localShip?.alive ?? false);
  const remotePlayers = useGameStore((s) => s.remotePlayers);

  return (
    <>
      {/* Scene background: dark blue-black (must be direct scene child, not in a group) */}
      <color attach="background" args={['#050510']} />

      <TopDownCamera />

      <hemisphereLight
        args={[HEMISPHERE_SKY_COLOR, HEMISPHERE_GROUND_COLOR, HEMISPHERE_INTENSITY]}
      />
      <directionalLight
        color={KEY_LIGHT_COLOR}
        position={KEY_LIGHT_POSITION}
        intensity={KEY_LIGHT_INTENSITY}
      />
      <directionalLight
        color={FILL_LIGHT_COLOR}
        position={FILL_LIGHT_POSITION}
        intensity={FILL_LIGHT_INTENSITY}
      />

      {/* Particle system manager (must be before any particle-emitting components) */}
      <ParticleManagerInit />

      <SpaceBackground />
      <SceneryRenderer />

      {/* World objects */}
      {worldSetup && (
        <>
          <Planet setup={worldSetup} />
          <AsteroidField setup={worldSetup} />
          <MapBorder halfSize={worldSetup.boundsHalfSize} />
        </>
      )}

      {/* Local player ship */}
      {localShipAlive && (
        <PlayerShip />
      )}

      {/* Remote player ships */}
      {Array.from(remotePlayers.values()).map((player) => (
        <RemoteShip
          key={player.entityId}
          state={player.state}
          stateTime={player.stateTime}
          username={player.username}
        />
      ))}

      {/* Projectile effects (tracers + impact flashes) */}
      <ProjectileEffects />
      <ImpactFlash />

      {/* Debug wireframes */}
      {debugWireframes && <DebugWireframes />}

      {/* Postprocessing pipeline — must be last child */}
      <PostprocessingPipeline />
    </>
  );
}
